import { useState, useEffect } from "react";
import { cartaPorteApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  Plus, Search, MoreVertical, Truck, Eye, CheckCircle, 
  XCircle, Trash2, Loader2, MapPin, Package, User,
  FileText, Calendar, AlertTriangle, Stamp
} from "lucide-react";

const CartaPortePage = () => {
  const [cartasPorte, setCartasPorte] = useState([]);
  const [catalogs, setCatalogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createDialog, setCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [step, setStep] = useState(1);

  const [newCartaPorte, setNewCartaPorte] = useState({
    tipo_comprobante: "T",
    tipo_transporte: "01",
    es_transporte_internacional: false,
    ubicaciones: [
      { tipo: "Origen", calle: "", numero_exterior: "", colonia: "", codigo_postal: "", municipio: "", estado: "", pais: "MEX", rfc_remitente_destinatario: "", nombre_remitente_destinatario: "" },
      { tipo: "Destino", calle: "", numero_exterior: "", colonia: "", codigo_postal: "", municipio: "", estado: "", pais: "MEX", distancia_recorrida: 0, rfc_remitente_destinatario: "", nombre_remitente_destinatario: "" }
    ],
    mercancias: [
      { clave_prod_serv: "", descripcion: "", cantidad: 1, clave_unidad: "H87", peso_kg: 0, valor_mercancia: 0, moneda: "MXN", es_material_peligroso: false }
    ],
    autotransporte: {
      permiso_sct: "TPAF01",
      num_permiso_sct: "",
      vehiculo: {
        config_vehicular: "C2",
        placa: "",
        anio_modelo: new Date().getFullYear(),
        aseguradora_resp_civil: "",
        poliza_resp_civil: "",
        remolque_subtipo: "",
        remolque_placa: ""
      }
    },
    figuras: [
      { tipo_figura: "01", rfc: "", nombre: "", num_licencia: "" }
    ]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [cartasRes, catalogsRes] = await Promise.all([
        cartaPorteApi.list(),
        cartaPorteApi.getCatalogs()
      ]);
      setCartasPorte(cartasRes.data.cartas_porte || []);
      setCatalogs(catalogsRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCartaPorte = async () => {
    // Validate required fields
    const origen = newCartaPorte.ubicaciones[0];
    const destino = newCartaPorte.ubicaciones[1];
    
    if (!origen.calle || !origen.codigo_postal || !origen.estado) {
      toast.error("Completa los datos del origen");
      setStep(1);
      return;
    }
    if (!destino.calle || !destino.codigo_postal || !destino.estado) {
      toast.error("Completa los datos del destino");
      setStep(1);
      return;
    }
    if (!newCartaPorte.mercancias[0].descripcion || !newCartaPorte.mercancias[0].peso_kg) {
      toast.error("Completa los datos de la mercancia");
      setStep(2);
      return;
    }
    if (!newCartaPorte.autotransporte.vehiculo.placa) {
      toast.error("Ingresa la placa del vehiculo");
      setStep(3);
      return;
    }
    if (!newCartaPorte.figuras[0].rfc || !newCartaPorte.figuras[0].nombre) {
      toast.error("Completa los datos del operador");
      setStep(4);
      return;
    }

    setCreating(true);
    try {
      await cartaPorteApi.create(newCartaPorte);
      toast.success("Carta Porte creada exitosamente");
      setCreateDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear Carta Porte");
    } finally {
      setCreating(false);
    }
  };

  const handleIssue = async (id) => {
    try {
      await cartaPorteApi.issue(id);
      toast.success("Carta Porte timbrada exitosamente");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al timbrar");
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Estas seguro de cancelar esta Carta Porte?")) return;
    try {
      await cartaPorteApi.cancel(id, "02");
      toast.success("Carta Porte cancelada");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cancelar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Estas seguro de eliminar esta Carta Porte?")) return;
    try {
      await cartaPorteApi.delete(id);
      toast.success("Carta Porte eliminada");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const resetForm = () => {
    setStep(1);
    setNewCartaPorte({
      tipo_comprobante: "T",
      tipo_transporte: "01",
      es_transporte_internacional: false,
      ubicaciones: [
        { tipo: "Origen", calle: "", numero_exterior: "", colonia: "", codigo_postal: "", municipio: "", estado: "", pais: "MEX", rfc_remitente_destinatario: "", nombre_remitente_destinatario: "" },
        { tipo: "Destino", calle: "", numero_exterior: "", colonia: "", codigo_postal: "", municipio: "", estado: "", pais: "MEX", distancia_recorrida: 0, rfc_remitente_destinatario: "", nombre_remitente_destinatario: "" }
      ],
      mercancias: [
        { clave_prod_serv: "", descripcion: "", cantidad: 1, clave_unidad: "H87", peso_kg: 0, valor_mercancia: 0, moneda: "MXN", es_material_peligroso: false }
      ],
      autotransporte: {
        permiso_sct: "TPAF01",
        num_permiso_sct: "",
        vehiculo: {
          config_vehicular: "C2",
          placa: "",
          anio_modelo: new Date().getFullYear(),
          aseguradora_resp_civil: "",
          poliza_resp_civil: "",
          remolque_subtipo: "",
          remolque_placa: ""
        }
      },
      figuras: [
        { tipo_figura: "01", rfc: "", nombre: "", num_licencia: "" }
      ]
    });
  };

  const updateUbicacion = (index, field, value) => {
    const ubicaciones = [...newCartaPorte.ubicaciones];
    ubicaciones[index][field] = value;
    setNewCartaPorte({ ...newCartaPorte, ubicaciones });
  };

  const updateMercancia = (index, field, value) => {
    const mercancias = [...newCartaPorte.mercancias];
    mercancias[index][field] = value;
    setNewCartaPorte({ ...newCartaPorte, mercancias });
  };

  const updateFigura = (index, field, value) => {
    const figuras = [...newCartaPorte.figuras];
    figuras[index][field] = value;
    setNewCartaPorte({ ...newCartaPorte, figuras });
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-slate-100 text-slate-700",
      issued: "bg-green-100 text-green-700",
      cancelled: "bg-red-100 text-red-700"
    };
    const labels = {
      draft: "Borrador",
      issued: "Timbrada",
      cancelled: "Cancelada"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  const filteredCartas = cartasPorte.filter(carta => {
    const matchesSearch = 
      carta.carta_porte_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || carta.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="carta-porte-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Carta Porte 3.1</h1>
          <p className="text-slate-600">Complemento obligatorio para transporte de mercancias</p>
        </div>
        <Button onClick={() => setCreateDialog(true)} data-testid="create-carta-porte-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Carta Porte
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="flex items-start gap-3 pt-6">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Complemento Carta Porte 3.1</p>
            <p className="text-sm text-amber-700 mt-1">
              Obligatorio para el traslado de bienes y/o mercancias en territorio nacional por cualquier 
              medio de transporte. Aplica a partir del 1 de enero de 2024.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{cartasPorte.length}</p>
                <p className="text-sm text-slate-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{cartasPorte.filter(c => c.status === 'issued').length}</p>
                <p className="text-sm text-slate-500">Timbradas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{cartasPorte.filter(c => c.status === 'draft').length}</p>
                <p className="text-sm text-slate-500">Borradores</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{cartasPorte.filter(c => c.status === 'cancelled').length}</p>
                <p className="text-sm text-slate-500">Canceladas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por IdCCP..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-carta-porte"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm"
          data-testid="status-filter"
        >
          <option value="all">Todos los estados</option>
          <option value="draft">Borrador</option>
          <option value="issued">Timbrada</option>
          <option value="cancelled">Cancelada</option>
        </select>
      </div>

      {/* Cartas Porte Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">IdCCP</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Tipo</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Mercancias</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Distancia</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Estado</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredCartas.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-slate-500">
                      <Truck className="w-12 h-12 mx-auto mb-4 opacity-20" />
                      <p>No hay Cartas Porte</p>
                      <Button variant="link" onClick={() => setCreateDialog(true)}>
                        Crear primera Carta Porte
                      </Button>
                    </td>
                  </tr>
                ) : (
                  filteredCartas.map((carta) => (
                    <tr key={carta.id} className="hover:bg-slate-50" data-testid={`carta-row-${carta.id}`}>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs">{carta.carta_porte_id?.substring(0, 20)}...</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{carta.tipo_comprobante === 'T' ? 'Traslado' : 'Ingreso'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{carta.num_total_mercancias} ({carta.peso_bruto_total} kg)</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm">{carta.total_dist_rec} km</span>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(carta.status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`carta-actions-${carta.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {}}>
                                <Eye className="w-4 h-4 mr-2" />
                                Ver detalle
                              </DropdownMenuItem>
                              
                              {carta.status === 'draft' && (
                                <>
                                  <DropdownMenuItem onClick={() => handleIssue(carta.id)}>
                                    <Stamp className="w-4 h-4 mr-2" />
                                    Timbrar
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleDelete(carta.id)} className="text-red-600">
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Eliminar
                                  </DropdownMenuItem>
                                </>
                              )}
                              
                              {carta.status === 'issued' && (
                                <DropdownMenuItem onClick={() => handleCancel(carta.id)} className="text-red-600">
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Cancelar
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Carta Porte Dialog */}
      <Dialog open={createDialog} onOpenChange={(open) => { setCreateDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva Carta Porte 3.1</DialogTitle>
          </DialogHeader>
          
          {/* Steps Indicator */}
          <div className="flex items-center justify-center gap-2 py-4">
            {[1, 2, 3, 4].map((s) => (
              <button
                key={s}
                onClick={() => setStep(s)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  step === s 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {s === 1 && <MapPin className="w-4 h-4" />}
                {s === 2 && <Package className="w-4 h-4" />}
                {s === 3 && <Truck className="w-4 h-4" />}
                {s === 4 && <User className="w-4 h-4" />}
                {s === 1 && "Ubicaciones"}
                {s === 2 && "Mercancias"}
                {s === 3 && "Vehiculo"}
                {s === 4 && "Operador"}
              </button>
            ))}
          </div>

          <div className="py-4 min-h-[400px]">
            {/* Step 1: Ubicaciones */}
            {step === 1 && (
              <div className="space-y-6">
                <h3 className="font-semibold flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-green-600" />
                  Origen
                </h3>
                <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
                  <div className="space-y-2">
                    <Label>Calle *</Label>
                    <Input
                      value={newCartaPorte.ubicaciones[0].calle}
                      onChange={(e) => updateUbicacion(0, 'calle', e.target.value)}
                      placeholder="Av. Principal"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Numero Exterior</Label>
                    <Input
                      value={newCartaPorte.ubicaciones[0].numero_exterior}
                      onChange={(e) => updateUbicacion(0, 'numero_exterior', e.target.value)}
                      placeholder="123"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Colonia</Label>
                    <Input
                      value={newCartaPorte.ubicaciones[0].colonia}
                      onChange={(e) => updateUbicacion(0, 'colonia', e.target.value)}
                      placeholder="Centro"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Codigo Postal *</Label>
                    <Input
                      value={newCartaPorte.ubicaciones[0].codigo_postal}
                      onChange={(e) => updateUbicacion(0, 'codigo_postal', e.target.value)}
                      placeholder="44100"
                      maxLength={5}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Estado *</Label>
                    <select
                      value={newCartaPorte.ubicaciones[0].estado}
                      onChange={(e) => updateUbicacion(0, 'estado', e.target.value)}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      <option value="">Seleccionar...</option>
                      {catalogs?.estados && Object.entries(catalogs.estados).map(([code, name]) => (
                        <option key={code} value={code}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>RFC Remitente</Label>
                    <Input
                      value={newCartaPorte.ubicaciones[0].rfc_remitente_destinatario}
                      onChange={(e) => updateUbicacion(0, 'rfc_remitente_destinatario', e.target.value.toUpperCase())}
                      placeholder="XAXX010101000"
                      maxLength={13}
                    />
                  </div>
                </div>

                <h3 className="font-semibold flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-red-600" />
                  Destino
                </h3>
                <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
                  <div className="space-y-2">
                    <Label>Calle *</Label>
                    <Input
                      value={newCartaPorte.ubicaciones[1].calle}
                      onChange={(e) => updateUbicacion(1, 'calle', e.target.value)}
                      placeholder="Blvd. Industrial"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Numero Exterior</Label>
                    <Input
                      value={newCartaPorte.ubicaciones[1].numero_exterior}
                      onChange={(e) => updateUbicacion(1, 'numero_exterior', e.target.value)}
                      placeholder="456"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Colonia</Label>
                    <Input
                      value={newCartaPorte.ubicaciones[1].colonia}
                      onChange={(e) => updateUbicacion(1, 'colonia', e.target.value)}
                      placeholder="Industrial"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Codigo Postal *</Label>
                    <Input
                      value={newCartaPorte.ubicaciones[1].codigo_postal}
                      onChange={(e) => updateUbicacion(1, 'codigo_postal', e.target.value)}
                      placeholder="06600"
                      maxLength={5}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Estado *</Label>
                    <select
                      value={newCartaPorte.ubicaciones[1].estado}
                      onChange={(e) => updateUbicacion(1, 'estado', e.target.value)}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      <option value="">Seleccionar...</option>
                      {catalogs?.estados && Object.entries(catalogs.estados).map(([code, name]) => (
                        <option key={code} value={code}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Distancia (km) *</Label>
                    <Input
                      type="number"
                      value={newCartaPorte.ubicaciones[1].distancia_recorrida}
                      onChange={(e) => updateUbicacion(1, 'distancia_recorrida', parseFloat(e.target.value) || 0)}
                      placeholder="150"
                      min={0}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Mercancias */}
            {step === 2 && (
              <div className="space-y-6">
                <h3 className="font-semibold flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Mercancias
                </h3>
                <div className="p-4 border rounded-lg space-y-4">
                  <div className="space-y-2">
                    <Label>Clave Producto SAT *</Label>
                    <Input
                      value={newCartaPorte.mercancias[0].clave_prod_serv}
                      onChange={(e) => updateMercancia(0, 'clave_prod_serv', e.target.value)}
                      placeholder="50101500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Descripcion *</Label>
                    <Input
                      value={newCartaPorte.mercancias[0].descripcion}
                      onChange={(e) => updateMercancia(0, 'descripcion', e.target.value)}
                      placeholder="Materiales de construccion"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Cantidad *</Label>
                      <Input
                        type="number"
                        value={newCartaPorte.mercancias[0].cantidad}
                        onChange={(e) => updateMercancia(0, 'cantidad', parseFloat(e.target.value) || 1)}
                        min={0.01}
                        step={0.01}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unidad</Label>
                      <select
                        value={newCartaPorte.mercancias[0].clave_unidad}
                        onChange={(e) => updateMercancia(0, 'clave_unidad', e.target.value)}
                        className="w-full border rounded-md px-3 py-2"
                      >
                        <option value="H87">H87 - Pieza</option>
                        <option value="KGM">KGM - Kilogramo</option>
                        <option value="LTR">LTR - Litro</option>
                        <option value="XBX">XBX - Caja</option>
                        <option value="XPK">XPK - Paquete</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Peso (kg) *</Label>
                      <Input
                        type="number"
                        value={newCartaPorte.mercancias[0].peso_kg}
                        onChange={(e) => updateMercancia(0, 'peso_kg', parseFloat(e.target.value) || 0)}
                        min={0}
                        step={0.001}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Valor (MXN)</Label>
                    <Input
                      type="number"
                      value={newCartaPorte.mercancias[0].valor_mercancia}
                      onChange={(e) => updateMercancia(0, 'valor_mercancia', parseFloat(e.target.value) || 0)}
                      min={0}
                      step={0.01}
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="material_peligroso"
                      checked={newCartaPorte.mercancias[0].es_material_peligroso}
                      onChange={(e) => updateMercancia(0, 'es_material_peligroso', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="material_peligroso">Es material peligroso</Label>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Vehiculo */}
            {step === 3 && (
              <div className="space-y-6">
                <h3 className="font-semibold flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Autotransporte Federal
                </h3>
                <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
                  <div className="space-y-2">
                    <Label>Tipo de Permiso SCT *</Label>
                    <select
                      value={newCartaPorte.autotransporte.permiso_sct}
                      onChange={(e) => setNewCartaPorte({
                        ...newCartaPorte,
                        autotransporte: { ...newCartaPorte.autotransporte, permiso_sct: e.target.value }
                      })}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      {catalogs?.tipos_permiso && Object.entries(catalogs.tipos_permiso).map(([code, name]) => (
                        <option key={code} value={code}>{code} - {name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Numero de Permiso SCT</Label>
                    <Input
                      value={newCartaPorte.autotransporte.num_permiso_sct}
                      onChange={(e) => setNewCartaPorte({
                        ...newCartaPorte,
                        autotransporte: { ...newCartaPorte.autotransporte, num_permiso_sct: e.target.value }
                      })}
                      placeholder="SCT123456"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Configuracion Vehicular *</Label>
                    <select
                      value={newCartaPorte.autotransporte.vehiculo.config_vehicular}
                      onChange={(e) => setNewCartaPorte({
                        ...newCartaPorte,
                        autotransporte: {
                          ...newCartaPorte.autotransporte,
                          vehiculo: { ...newCartaPorte.autotransporte.vehiculo, config_vehicular: e.target.value }
                        }
                      })}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      {catalogs?.config_vehicular && Object.entries(catalogs.config_vehicular).map(([code, name]) => (
                        <option key={code} value={code}>{code} - {name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Placa *</Label>
                    <Input
                      value={newCartaPorte.autotransporte.vehiculo.placa}
                      onChange={(e) => setNewCartaPorte({
                        ...newCartaPorte,
                        autotransporte: {
                          ...newCartaPorte.autotransporte,
                          vehiculo: { ...newCartaPorte.autotransporte.vehiculo, placa: e.target.value.toUpperCase() }
                        }
                      })}
                      placeholder="ABC-123-A"
                      maxLength={10}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ano Modelo</Label>
                    <Input
                      type="number"
                      value={newCartaPorte.autotransporte.vehiculo.anio_modelo}
                      onChange={(e) => setNewCartaPorte({
                        ...newCartaPorte,
                        autotransporte: {
                          ...newCartaPorte.autotransporte,
                          vehiculo: { ...newCartaPorte.autotransporte.vehiculo, anio_modelo: parseInt(e.target.value) || 2024 }
                        }
                      })}
                      min={1990}
                      max={2030}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Aseguradora Resp. Civil *</Label>
                    <Input
                      value={newCartaPorte.autotransporte.vehiculo.aseguradora_resp_civil}
                      onChange={(e) => setNewCartaPorte({
                        ...newCartaPorte,
                        autotransporte: {
                          ...newCartaPorte.autotransporte,
                          vehiculo: { ...newCartaPorte.autotransporte.vehiculo, aseguradora_resp_civil: e.target.value }
                        }
                      })}
                      placeholder="GNP Seguros"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Poliza Resp. Civil *</Label>
                    <Input
                      value={newCartaPorte.autotransporte.vehiculo.poliza_resp_civil}
                      onChange={(e) => setNewCartaPorte({
                        ...newCartaPorte,
                        autotransporte: {
                          ...newCartaPorte.autotransporte,
                          vehiculo: { ...newCartaPorte.autotransporte.vehiculo, poliza_resp_civil: e.target.value }
                        }
                      })}
                      placeholder="POL-123456"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Operador */}
            {step === 4 && (
              <div className="space-y-6">
                <h3 className="font-semibold flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Figura de Transporte (Operador)
                </h3>
                <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
                  <div className="space-y-2">
                    <Label>Tipo de Figura</Label>
                    <select
                      value={newCartaPorte.figuras[0].tipo_figura}
                      onChange={(e) => updateFigura(0, 'tipo_figura', e.target.value)}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      <option value="01">01 - Operador</option>
                      <option value="02">02 - Propietario</option>
                      <option value="03">03 - Arrendador</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>RFC *</Label>
                    <Input
                      value={newCartaPorte.figuras[0].rfc}
                      onChange={(e) => updateFigura(0, 'rfc', e.target.value.toUpperCase())}
                      placeholder="XAXX010101000"
                      maxLength={13}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre Completo *</Label>
                    <Input
                      value={newCartaPorte.figuras[0].nombre}
                      onChange={(e) => updateFigura(0, 'nombre', e.target.value)}
                      placeholder="Juan Perez Lopez"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Numero de Licencia</Label>
                    <Input
                      value={newCartaPorte.figuras[0].num_licencia}
                      onChange={(e) => updateFigura(0, 'num_licencia', e.target.value)}
                      placeholder="LIC123456"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-between">
            <div>
              {step > 1 && (
                <Button variant="outline" onClick={() => setStep(step - 1)}>
                  Anterior
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setCreateDialog(false); resetForm(); }}>
                Cancelar
              </Button>
              {step < 4 ? (
                <Button onClick={() => setStep(step + 1)}>
                  Siguiente
                </Button>
              ) : (
                <Button onClick={handleCreateCartaPorte} disabled={creating} data-testid="submit-carta-porte">
                  {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Crear Carta Porte
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CartaPortePage;
